import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Flame, Cpu, Wrench, Hammer, MoreHorizontal, UserSearch, Save, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const AREAS = [
  { id: "tig",        label: "Ručný TIG",   Icon: Flame },
  { id: "orbital",    label: "Orbitálne",   Icon: Cpu },
  { id: "pipefitter", label: "Potrubár",    Icon: Wrench },
  { id: "prep",       label: "Prípravár",   Icon: Hammer },
  { id: "recruiter",  label: "Nábor & HR",  Icon: UserSearch },
  { id: "other",      label: "Iné",         Icon: MoreHorizontal },
];

const LEVELS = [
  { id: "beginner",    label: "Začiatočník", desc: "Učím sa, prvé roky" },
  { id: "experienced", label: "Skúsený",     desc: "Pár rokov vo fachu, viem si poradiť" },
  { id: "expert",      label: "Expert",      desc: "Roky praxe, mentor pre iných" },
];

export default function PublicProfileEditor({ user, onSaved }) {
  const [areas, setAreas] = useState([]);
  const [level, setLevel] = useState("");
  const [years, setYears] = useState("");
  const [bio, setBio] = useState("");
  const [languages, setLanguages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!user) return;
    setAreas(user.expertise_areas || []);
    setLevel(user.expertise_level || "");
    setYears(user.years_experience != null ? String(user.years_experience) : "");
    setBio(user.bio || "");
    setLanguages(user.preferred_languages || []);
  }, [user?.id]);

  const toggleArea = (id) => {
    setAreas((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleLang = (code) => {
    setLanguages((prev) => prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const payload = {
        expertise_areas: areas,
        expertise_level: level || null,
        years_experience: years === "" ? null : parseInt(years, 10),
        bio: bio || null,
        preferred_languages: languages,
      };
      await api.patch("/auth/me/public-profile", payload);
      setMsg({ kind: "ok", text: "Verejný profil uložený. Vidia ho ostatní zvarači." });
      if (onSaved) onSaved();
    } catch (e) {
      setMsg({ kind: "err", text: e.response?.data?.detail || "Chyba pri ukladaní" });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} data-testid="public-profile-editor" className="border border-zinc-800 bg-[var(--wb-surface)] p-6 mb-6">
      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <div>
          <div className="font-display font-bold text-base">Verejný profil</div>
          <div className="text-xs text-zinc-500 mt-0.5">Vidia ho ostatní zvarači — pomôže im nájsť ťa keď budú potrebovať radu.</div>
        </div>
      </div>

      {/* Areas */}
      <div className="mt-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">V čom si dobrý (vyber všetko čo platí)</div>
        <div className="flex flex-wrap gap-2">
          {AREAS.map((a) => {
            const Icon = a.Icon;
            const active = areas.includes(a.id);
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => toggleArea(a.id)}
                data-testid={`area-toggle-${a.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs border transition-colors ${
                  active
                    ? "border-gold-600 bg-gold-950/40 text-gold-300"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                }`}
              >
                <Icon className="w-3 h-3" /> {a.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Level */}
      <div className="mt-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Tvoja úroveň</div>
        <div className="grid sm:grid-cols-3 gap-2">
          {LEVELS.map((lv) => {
            const active = level === lv.id;
            return (
              <button
                key={lv.id}
                type="button"
                onClick={() => setLevel(active ? "" : lv.id)}
                data-testid={`level-${lv.id}`}
                className={`text-left p-3 border transition-colors ${
                  active ? "border-gold-600 bg-gold-950/40" : "border-zinc-700 hover:border-zinc-500"
                }`}
              >
                <div className={`text-sm font-display font-bold ${active ? "text-gold-300" : "text-zinc-200"}`}>{lv.label}</div>
                <div className="text-[10px] text-zinc-500 mt-1">{lv.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Years */}
      <div className="mt-5">
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-2">Roky praxe</label>
        <input
          type="number"
          min="0"
          max="80"
          value={years}
          onChange={(e) => setYears(e.target.value)}
          data-testid="years-input"
          placeholder="0"
          className="w-32 bg-black border border-zinc-700 px-3 py-2 text-sm text-white focus:border-gold-600 outline-none"
        />
      </div>

      {/* Bio */}
      <div className="mt-5">
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-2">Krátka vizitka (max 400 znakov)</label>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value.slice(0, 400))}
          data-testid="bio-textarea"
          rows={3}
          placeholder="Napríklad: 30 rokov v zváraní, špecializácia tlakové potrubia, certifikát EN ISO 9606-1. Bratislava."
          className="w-full bg-black border border-zinc-700 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-gold-600 outline-none resize-none"
        />
        <div className="text-[10px] text-zinc-600 mt-1">{bio.length}/400</div>
      </div>

      {/* Preferred languages — for SOS routing and community flag display */}
      <div className="mt-5" data-testid="preferred-languages-section">
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Jazyky komunikácie</label>
        <div className="text-[11px] text-zinc-500 mb-2">Vyber všetky jazyky, ktorými chceš komunikovať. Ostatní uvidia vlajočky vedľa tvojho mena a SOS výzvy pôjdu len tým, čo hovoria tvojím jazykom.</div>
        <div className="flex flex-wrap gap-2">
          {[
            { code: "sk", flag: "🇸🇰", label: "Slovenčina" },
            { code: "en", flag: "🇬🇧", label: "English" },
            { code: "cs", flag: "🇨🇿", label: "Čeština" },
            { code: "de", flag: "🇩🇪", label: "Deutsch" },
            { code: "pl", flag: "🇵🇱", label: "Polski" },
          ].map((l) => {
            const active = languages.includes(l.code);
            return (
              <button
                key={l.code}
                type="button"
                onClick={() => toggleLang(l.code)}
                data-testid={`lang-toggle-${l.code}`}
                className={`px-3 py-1.5 text-xs font-mono inline-flex items-center gap-2 border transition-colors ${
                  active
                    ? "bg-gold-500 text-black border-gold-400"
                    : "bg-black text-zinc-400 border-zinc-700 hover:border-gold-600 hover:text-zinc-200"
                }`}
              >
                <span className="text-base">{l.flag}</span>
                <span>{l.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
        <button
          type="submit"
          disabled={busy}
          data-testid="save-public-profile"
          className="wb-btn-primary px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          Uložiť verejný profil
        </button>
        {msg && (
          <div className={`text-xs inline-flex items-center gap-1.5 ${msg.kind === "ok" ? "text-green-400" : "text-red-400"}`}>
            {msg.kind === "ok" ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
            {msg.text}
          </div>
        )}
      </div>
    </form>
  );
}
